import { MenuProps } from 'antd';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenSize } from '../../../hooks/useScreenSize';
import { DesktopSider } from './desktop';
import { MobileSider } from './mobile';
import {
    ICDashboard,
    ICFileInvoice,
    ICFileInvoiceSubmitted,
} from '../../../icons';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/reducers';
import { TeamOutlined, UserOutlined } from '@ant-design/icons';

const Wrapper = styled.div`
    .ant-menu-item,
    .ant-menu-submenu-title {
        gap: 0.5rem;
    }
`;

type MenuItem = Required<MenuProps>['items'][number];

type SiderAdminProps = {
    collapsed: boolean;
    setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
    openKeys: string[];
    setOpenKeys: React.Dispatch<React.SetStateAction<string[]>>;
};

export const SiderAdmin: React.FC<SiderAdminProps> = ({
    collapsed,
    setCollapsed,
    openKeys,
    setOpenKeys,
}) => {
    const size = useScreenSize();
    const mobileMode = size === 'xs' || size === 'sm';
    const navigate = useNavigate();
    const [selectedMenu, setSelectedMenu] = React.useState('');
    const userInfo = useSelector((state: RootState) => state.userInfo);

    const items: MenuItem[] = React.useMemo(() => {
        return [
            {
                key: 'dashboard',
                icon: (
                    <span
                        role="img"
                        aria-label="user"
                        className="anticon anticon-user ant-menu-item-icon"
                    >
                        <ICDashboard width={20} height={20} />
                    </span>
                ),
                label: 'Dashboard',
            },
            {
                key: 'pms_invoice',
                label: 'PMS E-Invoice',
                children: [
                    {
                        key: 'integrated_folios',
                        label: 'Integrated Folios',
                        icon: (
                            <span
                                role="img"
                                aria-label="user"
                                className="anticon anticon-user ant-menu-item-icon"
                            >
                                <ICFileInvoice width={20} height={20} />
                            </span>
                        ),
                    },
                    {
                        key: 'submitted_folios',
                        label: 'Submitted Folios',
                        icon: (
                            <span
                                role="img"
                                aria-label="user"
                                className="anticon anticon-user ant-menu-item-icon"
                            >
                                <ICFileInvoiceSubmitted
                                    width={20}
                                    height={20}
                                />
                            </span>
                        ),
                    },
                ],
            },
            {
                key: 'pos_invoice',
                label: 'POS E-Invoice',
                children: [
                    {
                        key: 'integrated_checks',
                        label: 'Integrated Checks',
                        icon: (
                            <span
                                role="img"
                                aria-label="user"
                                className="anticon anticon-user ant-menu-item-icon"
                            >
                                <ICFileInvoice width={20} height={20} />
                            </span>
                        ),
                    },
                    {
                        key: 'submitted_checks',
                        label: 'Submitted Checks',
                        icon: (
                            <span
                                role="img"
                                aria-label="user"
                                className="anticon anticon-user ant-menu-item-icon"
                            >
                                <ICFileInvoiceSubmitted
                                    width={20}
                                    height={20}
                                />
                            </span>
                        ),
                    },
                ],
            },
            {
                key: 'settings',
                label: 'Settings',
                children: [
                    {
                        key: 'user',
                        label: 'User',
                        icon: (
                            <span
                                role="img"
                                aria-label="user"
                                className="anticon anticon-user ant-menu-item-icon"
                            >
                                <UserOutlined
                                    style={{
                                        fontSize: '20px',
                                        color: 'currentColor',
                                    }}
                                />
                            </span>
                        ),
                    },
                    {
                        key: 'tenant',
                        label: 'Tenant',
                        icon: (
                            <span
                                role="img"
                                aria-label="user"
                                className="anticon anticon-user ant-menu-item-icon"
                            >
                                <TeamOutlined
                                    style={{
                                        fontSize: '20px',
                                        color: 'currentColor',
                                    }}
                                />
                            </span>
                        ),
                    },
                ],
            },
        ];
    }, [userInfo]);

    const onClickMenu: MenuProps['onClick'] = ({ key }) => {
        setSelectedMenu(key);
        setCollapsed(false);
        if (key === 'dashboard') {
            navigate('/dashboard');
        }
        if (key === 'integrated_folios') {
            navigate('/integrated-folios');
        }
        if (key === 'submitted_folios') {
            navigate('/submitted-folios');
        }
        if (key === 'integrated_checks') {
            navigate('/integrated-checks');
        }
        if (key === 'submitted_checks') {
            navigate('/submitted-checks');
        }
        if (key === 'user') {
            navigate('/settings/users');
        }
        if (key === 'tenant') {
            navigate('/settings/tenants');
        }
    };

    React.useEffect(() => {
        const path = window.location.pathname;
        setSelectedMenu('');
        if (path.includes('/dashboard')) {
            setSelectedMenu('dashboard');
        }
        if (path.includes('/integrated-folios')) {
            setSelectedMenu('integrated_folios');
        }
        if (path.includes('/submitted-folios')) {
            setSelectedMenu('submitted_folios');
        }
        if (path.includes('/integrated-checks')) {
            setSelectedMenu('integrated_checks');
        }
        if (path.includes('/submitted-checks')) {
            setSelectedMenu('submitted_checks');
        }
        if (path.includes('/settings/users')) {
            setSelectedMenu('user');
        }
        if (path.includes('/settings/tenants')) {
            setSelectedMenu('tenant');
        }
    }, []);

    React.useEffect(() => {
        if (!items.find((item: any) => item.key === window.location.pathname)) {
            const currentSubItem = items.find((item: any) =>
                item.children?.find((item: any) => {
                    return window.location.pathname.includes(item.key);
                })
            ) as any;
            if (currentSubItem) {
                // setOpenKeys([(currentSubItem['key'] as string) ?? '']);
                if (currentSubItem && currentSubItem['children']) {
                    const currentSubItemKey = currentSubItem['children']?.find(
                        (item: any) => {
                            return window.location.pathname.includes(item.key);
                        }
                    );
                    if (currentSubItemKey) {
                        setSelectedMenu(
                            (currentSubItemKey['key'] as string) ?? ''
                        );
                    }
                }
            }
            return;
        }
    }, [items]);

    return (
        <Wrapper className="flex">
            {mobileMode ? (
                <MobileSider
                    collapsed={collapsed}
                    setCollapsed={setCollapsed}
                    openKeys={openKeys}
                    items={items}
                    selectedMenu={selectedMenu}
                    onClickMenu={onClickMenu}
                    setOpenKeys={setOpenKeys}
                />
            ) : (
                <DesktopSider
                    collapsed={collapsed}
                    setCollapsed={setCollapsed}
                    openKeys={openKeys}
                    items={items}
                    selectedMenu={selectedMenu}
                    onClickMenu={onClickMenu}
                    setOpenKeys={setOpenKeys}
                />
            )}
        </Wrapper>
    );
};
